import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { Bar, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { RootReducerStateType } from '../../reducers';
import { AuthUserState } from '../../reducers/authUser';
import { getAuthUser } from '../../common/auth';
import { getFetcher } from '../../common/fetcher';

const BRENT_AI = 'brtPred';
const BRENT_INSIGHTS_URL = '/ai_analytics/brent_insights';
const POLL_INTERVAL_MS = 5 * 60 * 1000;

const formatNumber = (v: number | null | undefined, digits = 2, pct = false): string =>
    v === null || v === undefined ? '-' : v.toFixed(digits) + (pct ? '%' : '');

const BRENT_ANALYTICS_METRICS = [
    { label: 'Last Price', key: 'last_price', fmt: formatNumber },
    { label: 'Change %', key: 'change_pct', fmt: (v: number | null) => formatNumber(v, 2, true) },
    { label: 'ATR 1h', key: 'atr_1h', fmt: formatNumber },
    { label: 'Nearest Support', key: 'nearest_support', fmt: formatNumber },
    { label: 'Nearest Resistance', key: 'nearest_resistance', fmt: formatNumber },
    { label: 'Day Low', key: 'day_low', fmt: formatNumber },
    { label: 'Day High', key: 'day_high', fmt: formatNumber },
    { label: '1h Trend %', key: 'trend_pct_last_hour', fmt: (v: number | null) => formatNumber(v, 2, true) },
    { label: 'Intraday Volatility %', key: 'volatility_pct_intraday', fmt: (v: number | null) => formatNumber(v, 2, true) },
    { label: 'As of', key: 'as_of_epoch', fmt: (v: number | null) => (v ? new Date(v * 1000).toLocaleTimeString() : '-') },
    { label: 'Contract Month', key: 'contract_month', fmt: (v: string | null) => v || '-' },
    { label: 'Currency', key: 'currency', fmt: (v: string | null) => v || '-' },
    { label: 'Previous Close', key: 'previous_close', fmt: formatNumber },
    { label: 'Intraday Range', key: 'intraday_range', fmt: formatNumber },
    { label: 'Number of Bars', key: 'num_bars', fmt: (v: number | null) => (v !== null ? v.toString() : '-') },
    {
        label: 'Breakout (20d)',
        key: 'breakout_20d',
        fmt: (v: number | null) => (v === null ? '-' : v > 0 ? 'Up' : v < 0 ? 'Down' : 'Flat'),
    },
    { label: 'EMA 9d', key: 'ema_9d', fmt: formatNumber },
    { label: 'Momentum 1m', key: 'momentum_1m', fmt: formatNumber },
    { label: 'Momentum 3m', key: 'momentum_3m', fmt: formatNumber },
    { label: 'Momentum 6m', key: 'momentum_6m', fmt: formatNumber },
    { label: 'Momentum 12m', key: 'momentum_12m', fmt: formatNumber },
    { label: 'Moving Avg 50d', key: 'moving_avg_50d', fmt: formatNumber },
    { label: 'Moving Avg 200d', key: 'moving_avg_200d', fmt: formatNumber },
    {
        label: 'Moving Avg Trend',
        key: 'moving_avg_trend',
        fmt: (v: number | null) => (v === null ? '-' : v > 0 ? 'Up' : v < 0 ? 'Down' : 'Flat'),
    },
    { label: 'Contract Rolls After', key: 'contract_rolls_after', fmt: (v: string | null) => v || '-' },
];

interface BrentAnalysis {
    symbol: string;
    contract_month?: string;
    contract_rolls_after?: string | null;
    currency: string;
    as_of_epoch: number | null;
    last_price: number | null;
    previous_close: number | null;
    change_abs: number | null;
    change_pct: number | null;
    day_high: number | null;
    day_low: number | null;
    intraday_range: number | null;
    trend_pct_last_hour: number | null;
    volatility_pct_intraday: number | null;
    num_bars: number;
    nearest_support?: number | null;
    nearest_resistance?: number | null;
    supports?: number[];
    resistances?: number[];
    atr_1h?: number | null;
}

interface OhlcBar {
    ts: number;
    o: number;
    h: number;
    l: number;
    c: number;
    v?: number;
}

interface BrentHorizonAccuracy {
    hit_rate: number | null;
    n: number;
    overall_hit_rate: number | null;
    overall_n: number;
    current_call: string | null;
    current_streak: { call: string; since: string | null; count: number } | null;
}

interface BrentAccuracy {
    lookback_days: number;
    '2h': BrentHorizonAccuracy;
    '1d': BrentHorizonAccuracy;
}

interface BrentInsightsResponse {
    analysis?: BrentAnalysis;
    insights?: string | null;
    recommendation_2h?: string | null;
    recommendation_1d?: string | null;
    news_brief?: string | null;
    insights_generated_at?: string | null;
    insights_last_price?: number | null;
    generated_at_epoch?: number;
    ohlc_1h?: OhlcBar[];
    accuracy?: BrentAccuracy;
    error?: string;
}

const HORIZON_KEYS: Array<{ key: '2h' | '1d'; label: string }> = [
    { key: '2h', label: '2h' },
    { key: '1d', label: '1d' },
];
// Hide a hit rate computed from fewer than this many scored predictions — it's noise.
const MIN_SAMPLES_FOR_RATE = 10;

const RECOMMENDATION_COLORS: Record<string, string> = {
    buy: 'success.main',
    sell: 'error.main',
    neutral: 'text.secondary',
};

const normalizeRecommendation = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    return raw.toLowerCase().replace(/[^a-z]+$/, '') || null;
};

// The model ends its insights with two labeled lines (`2H: <call>` / `1D: <call>`).
// Strip those (and any trailing blanks) so the modal shows only the prose body.
const stripHorizonCalls = (insights: string | null | undefined): string => {
    if (!insights) return '';
    const lines = insights.replace(/\r/g, '').split('\n');
    const isCallLine = (line: string) => /^\s*(2H|1D)\s*:\s*(buy|sell|neutral)\s*$/i.test(line);
    while (lines.length && (isCallLine(lines[lines.length - 1]) || lines[lines.length - 1].trim() === '')) {
        lines.pop();
    }
    return lines.join('\n').trim();
};

const fmt = (v: number | null | undefined, digits = 2): string => (v === null || v === undefined ? '–' : v.toFixed(digits));

const fmtPct = (v: number | null | undefined, digits = 1): string =>
    v === null || v === undefined ? '–' : (v * 100).toFixed(digits) + '%';

const fmtTime = (epoch: number | null | undefined): string => {
    if (!epoch) return '–';
    return new Date(epoch * 1000).toLocaleTimeString();
};

const fmtBarTs = (ts: number): string => {
    const d = new Date(ts * 1000);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    return `${mm}/${dd} ${hh}:00`;
};

// Custom shape that draws an OHLC candle for a recharts Bar. The Bar's data
// accessor returns [low, high] so recharts gives us pixel coords spanning the
// full wick range; we derive the body rect (open→close) from that range.
const Candle = (props: any) => {
    const { x, y, width, height, payload } = props;
    if (!payload || typeof payload.o !== 'number') return null;
    const { o, h, l, c } = payload as OhlcBar;
    if (h === l) return null;
    const pxPerPrice = height / (h - l);
    const yOpen = y + (h - o) * pxPerPrice;
    const yClose = y + (h - c) * pxPerPrice;
    const bodyTop = Math.min(yOpen, yClose);
    const bodyHeight = Math.max(1, Math.abs(yClose - yOpen));
    const isUp = c >= o;
    const color = isUp ? '#26a69a' : '#ef5350';
    const wickX = x + width / 2;
    const bodyWidth = Math.max(1, width * 0.7);
    const bodyX = x + (width - bodyWidth) / 2;
    return (
        <g>
            <line x1={wickX} x2={wickX} y1={y} y2={y + height} stroke={color} strokeWidth={1} />
            <rect x={bodyX} y={bodyTop} width={bodyWidth} height={bodyHeight} fill={color} stroke={color} />
        </g>
    );
};

const CandleTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const bar = payload[0].payload as OhlcBar;
    return (
        <Box sx={{ backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, fontSize: '11px' }}>
            <div>{fmtBarTs(bar.ts)}</div>
            <div>O {fmt(bar.o)}</div>
            <div>H {fmt(bar.h)}</div>
            <div>L {fmt(bar.l)}</div>
            <div>C {fmt(bar.c)}</div>
        </Box>
    );
};

interface BrentChartProps {
    bars: OhlcBar[];
    supports: number[];
    resistances: number[];
    nearestSupport?: number | null;
    nearestResistance?: number | null;
}

const BrentChart = ({ bars, supports, resistances, nearestSupport, nearestResistance }: BrentChartProps) => {
    if (!bars.length) {
        return (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No 1h OHLC data available.
            </Typography>
        );
    }
    const data = bars.map((b) => ({ ...b, hl: [b.l, b.h] as [number, number] }));
    const tickInterval = Math.max(0, Math.floor(bars.length / 6) - 1);
    const lows = bars.map((b) => b.l);
    const highs = bars.map((b) => b.h);
    const allLevels = [...supports, ...resistances];
    const yMin = Math.min(...lows, ...allLevels);
    const yMax = Math.max(...highs, ...allLevels);
    const pad = (yMax - yMin) * 0.05 || 0.5;
    return (
        <Box sx={{ width: '100%', height: 420 }}>
            <ResponsiveContainer>
                <ComposedChart data={data} margin={{ top: 10, right: 60, bottom: 20, left: 10 }}>
                    <XAxis dataKey="ts" tickFormatter={fmtBarTs} interval={tickInterval} tick={{ fontSize: 10 }} minTickGap={20} />
                    <YAxis
                        domain={[yMin - pad, yMax + pad]}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: number) => v.toFixed(2)}
                        width={50}
                    />
                    <Tooltip content={<CandleTooltip />} />
                    <Bar dataKey="hl" shape={<Candle />} isAnimationActive={false} />
                    {supports.map((price) => {
                        const isNearest = nearestSupport != null && price === nearestSupport;
                        return (
                            <ReferenceLine
                                key={`s-${price}`}
                                y={price}
                                stroke="#26a69a"
                                strokeWidth={isNearest ? 2 : 1}
                                strokeDasharray={isNearest ? undefined : '4 4'}
                                label={{ value: price.toFixed(2), position: 'right', fontSize: 10, fill: '#26a69a' }}
                            />
                        );
                    })}
                    {resistances.map((price) => {
                        const isNearest = nearestResistance != null && price === nearestResistance;
                        return (
                            <ReferenceLine
                                key={`r-${price}`}
                                y={price}
                                stroke="#ef5350"
                                strokeWidth={isNearest ? 2 : 1}
                                strokeDasharray={isNearest ? undefined : '4 4'}
                                label={{ value: price.toFixed(2), position: 'right', fontSize: 10, fill: '#ef5350' }}
                            />
                        );
                    })}
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
};

const AiPanel = () => {
    const [aiModel, setAiModel] = useState('');
    const [data, setData] = useState<BrentInsightsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [insightsModalOpen, setInsightsModalOpen] = useState(false);
    const [metricsModalOpen, setMetricsModalOpen] = useState(false);
    const [newsModalOpen, setNewsModalOpen] = useState(false);
    const [chartModalOpen, setChartModalOpen] = useState(false);
    const [accuracyModalOpen, setAccuracyModalOpen] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const authUserState = useSelector<RootReducerStateType, AuthUserState>((state) => state.authUser, shallowEqual);

    const fetchInsights = useCallback(() => {
        setLoading(true);
        setError(null);
        getFetcher<BrentInsightsResponse>({
            url: BRENT_INSIGHTS_URL,
            headers: { 'Swiss-Tech-Auth-Token': getAuthUser(authUserState).swiss_token },
            onSuccess: (res) => {
                setData(res);
                setLoading(false);
            },
            onFail: (err) => {
                setError(err.parsedMsg);
                setLoading(false);
            },
        });
    }, [authUserState]);

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (aiModel !== BRENT_AI) {
            setData(null);
            setError(null);
            setLoading(false);
            return;
        }
        fetchInsights();
        intervalRef.current = setInterval(fetchInsights, POLL_INTERVAL_MS);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [aiModel, fetchInsights]);

    const handleAiModelChange = (event: SelectChangeEvent) => {
        setAiModel(event.target.value);
    };

    const analysis = data?.analysis;
    const changePct = analysis?.change_pct;
    const changeColor = changePct === null || changePct === undefined ? 'inherit' : changePct >= 0 ? 'success.main' : 'error.main';
    const recommendations: Array<{ label: string; call: string | null }> = [
        { label: '2h', call: normalizeRecommendation(data?.recommendation_2h) },
        { label: '1d', call: normalizeRecommendation(data?.recommendation_1d) },
    ];
    const hasRecommendation = recommendations.some((r) => r.call);
    const recommendationColor = (call: string | null): string =>
        call && call in RECOMMENDATION_COLORS ? RECOMMENDATION_COLORS[call] : 'text.secondary';
    const insightsBody = stripHorizonCalls(data?.insights);
    const newsBrief = data?.news_brief;
    const accuracy = data?.accuracy;

    return (
        <Paper sx={{ backgroundColor: 'transparent', minHeight: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                <FormControl variant="standard" sx={{ m: 1, minWidth: 80 }}>
                    <InputLabel id="ai-panel-select-label" sx={{ fontSize: '12px' }}>
                        AI Model
                    </InputLabel>
                    <Select id="ai-panel-select" value={aiModel} onChange={handleAiModelChange} label="aiModel" sx={{ fontSize: '12px' }}>
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        <MenuItem value={BRENT_AI}>Brent AI</MenuItem>
                    </Select>
                </FormControl>
                {accuracy && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setAccuracyModalOpen(true)}
                        sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none' }}>
                        Accuracy
                    </Button>
                )}
            </Box>
            {aiModel === BRENT_AI && (
                <Box sx={{ px: 1, pb: 1, fontSize: '12px', textAlign: 'left' }}>
                    {loading && !data && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={14} />
                            <Typography variant="caption">Fetching Brent insights…</Typography>
                        </Box>
                    )}
                    {error && (
                        <Typography variant="caption" color="error.main">
                            {error}
                        </Typography>
                    )}
                    {data?.error && (
                        <Typography variant="caption" color="error.main">
                            {data.error}
                        </Typography>
                    )}
                    {analysis && (
                        <Box>
                            <Typography variant="caption" component="div">
                                {analysis.contract_month} Brent {fmt(analysis.last_price)} {analysis.currency}{' '}
                                <Box component="span" sx={{ color: changeColor }}>
                                    ({changePct !== null && changePct !== undefined && changePct >= 0 ? '+' : ''}
                                    {fmt(changePct)}%)
                                </Box>
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                                Day {fmt(analysis.day_low)}–{fmt(analysis.day_high)} · 1h {fmt(analysis.trend_pct_last_hour)}% · vol{' '}
                                {fmt(analysis.volatility_pct_intraday)}% · as of {fmtTime(analysis.as_of_epoch)}
                            </Typography>
                        </Box>
                    )}
                    {hasRecommendation && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 1 }}>
                            {recommendations.map((rec) => (
                                <Typography
                                    key={rec.label}
                                    variant="caption"
                                    component="div"
                                    sx={{ fontWeight: 'bold', color: recommendationColor(rec.call) }}>
                                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 'normal', textTransform: 'none' }}>
                                        {rec.label}:{' '}
                                    </Box>
                                    <Box component="span" sx={{ textTransform: 'uppercase' }}>
                                        {rec.call ?? '–'}
                                    </Box>
                                </Typography>
                            ))}
                        </Box>
                    )}
                    {data?.insights && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setInsightsModalOpen(true)}
                            sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none', marginRight: '8px' }}>
                            Read AI insights
                        </Button>
                    )}
                    {analysis && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setMetricsModalOpen(true)}
                            sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none', marginRight: '8px' }}>
                            Metrics
                        </Button>
                    )}
                    {analysis && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setNewsModalOpen(true)}
                            sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none', marginRight: '8px' }}>
                            News
                        </Button>
                    )}
                    {data?.ohlc_1h && data.ohlc_1h.length > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setChartModalOpen(true)}
                            sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none' }}>
                            Chart
                        </Button>
                    )}
                    {data && !data.insights && !data.error && (
                        <Typography variant="caption" component="div" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                            (Gemini prompt not configured)
                        </Typography>
                    )}
                </Box>
            )}
            <Dialog open={metricsModalOpen} onClose={() => setMetricsModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontSize: '14px' }}>Metrics</DialogTitle>
                <DialogContent dividers>
                    {analysis && (
                        <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                            {BRENT_ANALYTICS_METRICS.map((metric) => `${metric.label}: ${metric.fmt((analysis as any)[metric.key])}`).join(
                                '\n'
                            )}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button size="small" onClick={() => setMetricsModalOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={insightsModalOpen} onClose={() => setInsightsModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontSize: '14px' }}>Brent AI insights</DialogTitle>
                <DialogContent dividers>
                    {insightsBody && (
                        <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                            {insightsBody}
                        </Typography>
                    )}
                    {hasRecommendation && (
                        <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                            {recommendations.map((rec) => (
                                <Typography
                                    key={rec.label}
                                    variant="body2"
                                    component="div"
                                    sx={{ fontWeight: 'bold', color: recommendationColor(rec.call) }}>
                                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>
                                        {rec.label}:{' '}
                                    </Box>
                                    <Box component="span" sx={{ textTransform: 'uppercase' }}>
                                        {rec.call ?? '–'}
                                    </Box>
                                </Typography>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button size="small" onClick={() => setInsightsModalOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={newsModalOpen} onClose={() => setNewsModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontSize: '14px' }}>Brent AI News</DialogTitle>
                <DialogContent dividers>
                    {newsBrief && (
                        <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                            {newsBrief}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button size="small" onClick={() => setNewsModalOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={chartModalOpen} onClose={() => setChartModalOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ fontSize: '14px' }}>Brent 1h chart with support / resistance</DialogTitle>
                <DialogContent dividers>
                    <BrentChart
                        bars={data?.ohlc_1h ?? []}
                        supports={analysis?.supports ?? []}
                        resistances={analysis?.resistances ?? []}
                        nearestSupport={analysis?.nearest_support}
                        nearestResistance={analysis?.nearest_resistance}
                    />
                </DialogContent>
                <DialogActions>
                    <Button size="small" onClick={() => setChartModalOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={accuracyModalOpen} onClose={() => setAccuracyModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontSize: '14px' }}>Accuracy</DialogTitle>
                <DialogContent dividers>
                    {accuracy && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Directional hit rate on buy/sell calls only. Each call is scored against the actual price at the horizon;
                                neutral (no-trade), inconclusive (too small to judge) and no-data outcomes are excluded. "Recent" covers the
                                last {accuracy.lookback_days} days; "All time" is the model's lifetime tally.
                            </Typography>
                            {HORIZON_KEYS.map(({ key, label }) => {
                                const stats = accuracy[key];
                                const enoughSamples = stats.n >= MIN_SAMPLES_FOR_RATE;
                                const enoughOverall = stats.overall_n >= MIN_SAMPLES_FOR_RATE;
                                const streak = stats.current_streak;
                                return (
                                    <Box key={key}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {label} horizon
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: enoughSamples ? 'text.primary' : 'text.disabled' }}>
                                            Recent ({accuracy.lookback_days}d): {enoughSamples ? fmtPct(stats.hit_rate) : '–'} ({stats.n}{' '}
                                            scored{enoughSamples ? '' : `, need ${MIN_SAMPLES_FOR_RATE}`})
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: enoughOverall ? 'text.primary' : 'text.disabled' }}>
                                            All time: {enoughOverall ? fmtPct(stats.overall_hit_rate) : '–'} ({stats.overall_n}{' '}
                                            scored{enoughOverall ? '' : `, need ${MIN_SAMPLES_FOR_RATE}`})
                                        </Typography>
                                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                            Current call: {stats.current_call ?? '–'}
                                            {streak && streak.count > 1 ? ` (×${streak.count})` : ''}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button size="small" onClick={() => setAccuracyModalOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default AiPanel;
